import { useId, useLayoutEffect, useRef, type FocusEvent, type Ref } from 'react'

import { Checkbox } from '@/ui/checkbox'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'

import type { PrinterCounterKind } from '../../api/assets-api'
import { counterLabel, type CatalogState } from './print-settings'

export type CounterCatalogState = CatalogState | 'forbidden'

export function inCatalogOrder(kindIds: readonly string[], catalog: readonly PrinterCounterKind[]) {
  const chosen = new Set(kindIds)
  const known = new Set(catalog.map((kind) => kind.kindId))
  return [
    ...catalog.map((kind) => kind.kindId).filter((kindId) => chosen.has(kindId)),
    ...[...chosen].filter((kindId) => !known.has(kindId)),
  ]
}

const catalogFailure = 'Couldn’t load counter choices.'

export function CounterKindPicker({
  value,
  options,
  onChange,
  onBlur,
  ref,
  disabled = false,
  labelledBy,
  describedBy,
  optionsState = 'ready',
  onRetry,
}: {
  value: string[]
  options: PrinterCounterKind[]
  onChange: (next: string[]) => void
  onBlur?: () => void
  ref?: Ref<HTMLDivElement>
  disabled?: boolean
  labelledBy: string
  describedBy?: string
  optionsState?: CounterCatalogState
  onRetry?: () => void
}) {
  const baseId = useId()
  const frameRef = useRef<HTMLDivElement | null>(null)
  const removedId = useRef<string | null>(null)
  const selected = new Set(value)
  const available = options.filter(
    (option, index) => options.findIndex((other) => other.kindId === option.kindId) === index,
  )
  const groups = new Map<string, PrinterCounterKind[]>()
  for (const option of available) groups.set(option.group, [...(groups.get(option.group) ?? []), option])
  const ready = optionsState === 'ready'
  const unavailable = ready
    ? [...selected].filter((id) => !available.some((option) => option.kindId === id))
    : []

  useLayoutEffect(() => {
    if (removedId.current === null) return
    if (!value.includes(removedId.current)) frameRef.current?.focus()
    removedId.current = null
  })

  function change(id: string, checked: boolean, missing = false) {
    if (missing && !checked) removedId.current = id
    const next = new Set(selected)
    if (checked) next.add(id)
    else next.delete(id)
    onChange(inCatalogOrder([...next], available))
  }

  function reportFocusLeaving(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) onBlur?.()
  }

  function choice(id: string, label: string, missing = false) {
    const checkboxId = `${baseId}-${id}`
    return (
      <label key={id} htmlFor={checkboxId} className="flex items-center gap-2 py-1 text-sm">
        <Checkbox
          id={checkboxId}
          className="border-muted-foreground"
          checked={selected.has(id)}
          disabled={disabled || !ready}
          onCheckedChange={(checked) => change(id, checked, missing)}
        />
        <span className="break-words">{label}</span>
      </label>
    )
  }

  return (
    <div
      ref={(node) => {
        frameRef.current = node
        if (typeof ref === 'function') return ref(node)
        if (ref) ref.current = node
      }}
      role="group"
      tabIndex={-1}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onBlur={reportFocusLeaving}
      className="border-border focus-visible:border-ring focus-visible:ring-ring/30 flex flex-col gap-3 rounded-lg border p-3 outline-none focus-visible:ring-2"
    >
      {[...(ready ? groups : [])].map(([group, kinds]) => (
        <fieldset key={group}>
          <legend className="text-muted-foreground mb-1 text-xs font-medium">
            {group.charAt(0).toUpperCase() + group.slice(1)}
          </legend>
          {kinds.map((kind) => choice(kind.kindId, kind.name))}
        </fieldset>
      ))}
      {!ready && selected.size > 0 ? (
        <fieldset>
          <legend className="text-muted-foreground mb-1 text-xs font-medium">
            Current selection
          </legend>
          {[...selected].map((id) => choice(id, counterLabel(id, options)))}
        </fieldset>
      ) : null}
      {unavailable.length > 0 ? (
        <fieldset>
          <legend className="text-muted-foreground mb-1 text-xs font-medium">
            Unavailable choices
          </legend>
          {unavailable.map((id) => choice(id, `${counterLabel(id, [])} (unavailable)`, true))}
        </fieldset>
      ) : null}
      <CatalogNotice state={optionsState} empty={available.length === 0} onRetry={onRetry} />
    </div>
  )
}

function CatalogNotice({
  state,
  empty,
  onRetry,
}: {
  state: CounterCatalogState
  empty: boolean
  onRetry?: () => void
}) {
  if (state === 'loading') return <LoadingNotice message="Loading counter choices…" />
  if (state === 'forbidden')
    return (
      <p className="text-muted-foreground text-sm">
        You need permission to view printers to change the counters.
      </p>
    )
  if (state === 'error' || state === 'retrying')
    return onRetry ? (
      <RetryNotice message={catalogFailure} onRetry={onRetry} disabled={state === 'retrying'} />
    ) : (
      <p role="alert" className="text-destructive text-sm">
        {catalogFailure}
      </p>
    )
  if (empty) return <p className="text-muted-foreground text-sm">No counter choices are available.</p>
  return null
}
